import React, { useState, useEffect, useContext } from 'react';
import { Box, Typography, Divider, Grid, Button, Drawer, useMediaQuery, CircularProgress } from '@mui/material';
import { Screen } from '../../components';
import { UserContext } from '../../lib/contexts/UserContext';
import { ThemeContext } from '../../lib/contexts/ThemeContext';
import { OrdersContext } from '../../lib/contexts/OrdersContext';
import { BenefitDetailsContext } from '../../lib/contexts/BenefitDetailsContext';
import NewProgramInfoCard from './components/NewProgramInfoCard';
import MiniInfoBlock from './components/subComponents/MiniInfoBlock';
import useUserAddress from '../../lib/hooks/useUserAddress';
import { prettyDate, toCapitalizeFromUnderScore } from '../../lib/helpers/helpers';
import MiniAddressCard from './components/subComponents/MiniAddressCard';
import EditAddressPopUp from './components/subComponents/EditAddressPopUp';
import PreferenceSwitchBox from './components/subComponents/PreferenceSwitchBox';
import AccountManagementScreenMobile from './AccountManagementScreenMobile';
import { formatAddress } from '../../lib/helpers/helpers';
import { determineUserFacingStatus } from './components/NewProgramInfoCard';
import useSession from '../../lib/hooks/useSession';

const AccountManagementScreen = () => {
  const { state } = useContext(UserContext);
  const { mainBenefitDetails, benefitDetails } = useContext(BenefitDetailsContext);
  const { data: userAddressData } = useUserAddress();
  const { data: session } = useSession();
  const [workflowData, setWorkflowData] = useState({});
  const [programBenefits, setProgramBenefits] = useState([]);
  const [applicationBenefits, setApplicationBenefits] = useState([]);
  const [menuOptions, setMenuOptions] = useState([
    'Personal Info',
    // 'Preferences',
    'Program History',
    'Application History',
  ]);
  const [selectedMenuOption, setSelectedMenuOption] = useState('Personal Info');
  const [orderedAddresses, setOrderedAddresses] = useState([]);
  const [showDefaultAddressMessage, setShowDefaultAddressMessage] = useState(false);
  const [contactInfo, setContactInfo] = useState({
    phoneNumber: '',
    email: '',
    primaryAddress: '',
  });
  const [healthInfo, setHealthInfo] = useState({
    healthPlan: '',
    chronicConditions: '',
    DOB: '',
  });
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isAddAddress, setIsAddAddress] = useState(false);

  // useEffect(() => {
  //   console.log('here is state: ', state);
  //   console.log('here is state?.user: ', state?.user);
  //   console.log('here is state?.user?.email: ', state?.user?.email);
  //   console.log('here is state?.email: ', state?.email);
  //   console.log('here is state?.user?.phone_number: ', state?.user?.phone_number);
  // }, [state]);

  // New state to track if default values should be set
  const [defaultContactInfo, setDefaultContactInfo] = useState(false);
  const [defaultHealthInfo, setDefaultHealthInfo] = useState(false);

  const isMobileView = useMediaQuery('(max-width: 600px)');

  useEffect(() => {
    const tempArr = [];
    let tempDefaultAddress = null;
    if (userAddressData?.locations) {
      for (let info of userAddressData?.locations) {
        if (info?.is_default === 1) {
          tempDefaultAddress = info;
        } else {
          tempArr.push(info);
        }
      }
    }
    setOrderedAddresses([tempDefaultAddress, ...tempArr]);
  }, [userAddressData]);

  useEffect(() => {
    const addressTimeout = setTimeout(() => {
      if (orderedAddresses.length === 0 || (orderedAddresses.length === 1 && orderedAddresses[0] === null)) {
        setShowDefaultAddressMessage(true);
      }
    }, 10000); // 10 seconds

    return () => clearTimeout(addressTimeout);
  }, [orderedAddresses]);

  useEffect(() => {
    if (session) {
      setWorkflowData({
        currentDietQuality: session?.lastQuality,
        goalDietQuality: session?.goalQuality,
      });
    }
  }, [session]);

  useEffect(() => {
    // console.log('state:', state);
    // console.log('userAddressData:', userAddressData);
    // console.log('mainBenefitDetails:', mainBenefitDetails);
    if (state && userAddressData?.locations) {
      let tempAddress = '';
      for (let info of userAddressData?.locations) {
        if (info?.is_default) {
          tempAddress = formatAddress(info);
        }
      }
      setContactInfo({
        phoneNumber: state?.phoneNumber,
        email: state?.email,
        primaryAddress: tempAddress,
      });
    } else if (state) {
      setContactInfo({
        ...contactInfo,
        phoneNumber: state?.phoneNumber,
        email: state?.email,
      });
    } else if (userAddressData?.locations) {
      setContactInfo({
        ...contactInfo,
        primaryAddress: tempAddress,
      });
    }
    if (state) {
      const tempGoalArr = state?.userObject?.lastWorkflow?.raw_responses?.ideal_goals?.for_health;
      let tempChronicConditionsString = '';
      for (let index in tempGoalArr) {
        if (index === tempGoalArr.length - 1) {
          tempChronicConditionsString += `${toCapitalizeFromUnderScore(tempGoalArr[index])}`;
        } else {
          tempChronicConditionsString += `${toCapitalizeFromUnderScore(tempGoalArr[index])}, `;
        }
      }
      const tempDOB = prettyDate(state?.DOB);
      setHealthInfo({
        chronicConditions: tempChronicConditionsString,
        DOB: tempDOB,
        healthPlan: mainBenefitDetails?.benefit_name,
      });
    }
  }, [state?.userObject, mainBenefitDetails, userAddressData]);

  useEffect(() => {
    const contactInfoTimeout = setTimeout(() => {
      setDefaultContactInfo(true);
    }, 10000); // 10 seconds

    const healthInfoTimeout = setTimeout(() => {
      setDefaultHealthInfo(true);
    }, 10000); // 10 seconds

    return () => {
      clearTimeout(contactInfoTimeout);
      clearTimeout(healthInfoTimeout);
    };
  }, []);

  const handleMenuClick = (option) => {
    setSelectedMenuOption(option);
  };

  const handleDrawerOpen = (addAddress = false) => {
    setIsAddAddress(addAddress);
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
  };

  useEffect(() => {
    benefitDetails?.forEach((benefit) => {
      const userFacingStatus = determineUserFacingStatus(benefit, workflowData);
      const type = ['Active', 'Canceled', 'Completed'].includes(userFacingStatus) ? 'Program' : 'Application';

      if (type === 'Program') {
        setProgramBenefits((prev) => [...prev, benefit]);
      } else {
        setApplicationBenefits((prev) => [...prev, benefit]);
      }
    });
  }, [benefitDetails, workflowData]);

  useEffect(() => {
    if (programBenefits.length === 0 && applicationBenefits.length === 0) {
      setMenuOptions(['Personal Info']);
    } else if (programBenefits.length > 0 && applicationBenefits.length === 0) {
      setMenuOptions(['Personal Info', 'Program History']);
    } else if (programBenefits.length === 0 && applicationBenefits.length > 0) {
      setMenuOptions(['Personal Info', 'Application History']);
    }
  }, [programBenefits, applicationBenefits]);

  if (isMobileView) {
    return <AccountManagementScreenMobile />;
  }

  return (
    <Screen showSidebarNav={true}>
      <Box
        id="account-management-screen-container"
        sx={{
          display: 'flex',
          width: '1056px',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '40px',
          margin: '0 auto',
        }}
      >
        <Box
          id="main-header-container"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '24px',
            alignSelf: 'stretch',
          }}
        >
          <Box
            id="header-title-container"
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              alignSelf: 'stretch',
            }}
          >
            <Box
              id="header-title-text-container"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '40px',
              }}
            >
              <Typography
                sx={{
                  color: 'var(--Greys-Black, #1B1B1B)',
                  fontFamily: 'Montserrat',
                  fontSize: '36px',
                  fontStyle: 'normal',
                  fontWeight: 800,
                  lineHeight: '125%', // 45px
                }}
              >
                Your Account
              </Typography>
            </Box>
            {(selectedMenuOption === 'Application History' || selectedMenuOption === 'Program History') && (
              <Box
                id="apply-for-food-button-container"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '24px',
                }}
              ></Box>
            )}
          </Box>
          <Box
            id="header-menu-container"
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '16px',
              alignSelf: 'stretch',
              borderBottom: '1px solid var(--Gray-gray5, #E8E8E8)',
            }}
          >
            {menuOptions.map((option, index) => (
              <Box
                key={index}
                id="header-menu-item-container"
                onClick={() => handleMenuClick(option)}
                sx={{
                  display: 'flex',
                  padding: '0px 8px 8px 8px',
                  alignItems: 'center',
                  gap: '16px',
                  cursor: 'pointer',
                  borderBottom:
                    selectedMenuOption === option ? '2px solid var(--Tangelo-Matcha-matcha10, #009B7D)' : 'none',
                }}
              >
                <Box
                  id="header-menu-item-text-container"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: '24.984px',
                  }}
                >
                  <Box
                    id="yet-another-menu-item-text-container"
                    sx={{
                      display: 'flex',
                      height: '29.147px',
                      alignItems: 'center',
                      gap: '10.41px',
                    }}
                  >
                    <Typography
                      sx={{
                        color:
                          selectedMenuOption === option
                            ? 'var(--Tangelo-Matcha-matcha11, #007E66)'
                            : 'var(--Black, #242424)',
                        leadingTrim: 'both',
                        textEdge: 'cap',
                        fontFamily: 'Montserrat',
                        fontSize: '16px',
                        fontStyle: 'normal',
                        fontWeight: 700,
                        lineHeight: 'normal',
                      }}
                    >
                      {option}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
        {selectedMenuOption === 'Personal Info' && (
          <Box
            id="main-personal-info-content-container"
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              alignContent: 'flex-start',
              gap: '40px',
              alignSelf: 'stretch',
              flexWrap: 'wrap',
            }}
          >
            <Box
              id="delivery-addresses-info-box-container"
              sx={{
                display: 'flex',
                width: '324px',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '16px',
                borderRadius: '8px',
              }}
            >
              <Typography
                sx={{
                  alignSelf: 'stretch',
                  color: 'var(--color12, #171717)',
                  fontSize: '20px',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  lineHeight: '125%', // 25px
                }}
              >
                Delivery Addresses
              </Typography>
              <Button
                id="add-address-button"
                sx={{
                  display: 'flex',
                  width: '100%',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 'var(--4, 18px)',
                  borderRadius: 'var(--4, 9px)',
                  border: 'var(--1, 2px) solid var(--color12, #1C124D)',
                }}
                onClick={() => handleDrawerOpen(true)}
              >
                <Typography
                  sx={{
                    color: 'var(--color12, #1C124D)',
                    textAlign: 'center',
                    fontWeight: 700,
                  }}
                >
                  Add New Address
                </Typography>
              </Button>
              <Box id="delivery-addresses-container">
                {showDefaultAddressMessage ? (
                  <Typography>No addresses found</Typography>
                ) : orderedAddresses.length === 0 || (orderedAddresses.length === 1 && orderedAddresses[0] === null) ? (
                  <CircularProgress />
                ) : (
                  orderedAddresses?.map((address, index) => (
                    <MiniAddressCard
                      key={index}
                      address={address}
                    />
                  ))
                )}
              </Box>
            </Box>
            <Box
              id="contact-info-box-container"
              sx={{
                display: 'flex',
                width: '324px',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '40px',
              }}
            >
              <Box
                id="contact-info-container"
                sx={{
                  display: 'flex',
                  width: '327px',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '16px',
                  borderRadius: '8px',
                }}
              >
                <Typography
                  sx={{
                    alignSelf: 'stretch',
                    color: 'var(--color12, #171717)',
                    fontFamily: 'Montserrat',
                    fontSize: '20px',
                    fontStyle: 'normal',
                    fontWeight: 700,
                    lineHeight: '125%', // 25px
                  }}
                >
                  Contact Info
                </Typography>
                <Box
                  id="info-boxes-container"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: '16px',
                    alignSelf: 'stretch',
                  }}
                >
                  <MiniInfoBlock
                    title="Phone Number"
                    type="phone"
                    value={
                      defaultContactInfo && !contactInfo.phoneNumber ? 'No Phone Number found' : contactInfo.phoneNumber
                    }
                  />
                  <MiniInfoBlock
                    title="Email"
                    type="email"
                    value={defaultContactInfo && !contactInfo.email ? 'No Email found' : contactInfo.email}
                  />
                  {/* <MiniInfoBlock
                    title="Primary Address"
                    value={defaultContactInfo && !contactInfo.primaryAddress ? 'No Primary Address found' : contactInfo.primaryAddress}
                  /> */}
                </Box>
              </Box>
            </Box>
            <Box
              id="health-info-box-container"
              sx={{
                display: 'flex',
                width: '327px',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '16px',
                borderRadius: '8px',
              }}
            >
              <Box
                id="health-info-container"
                sx={{
                  display: 'flex',
                  width: '327px',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '16px',
                  borderRadius: '8px',
                }}
              >
                <Typography
                  sx={{
                    alignSelf: 'stretch',
                    color: 'var(--color12, #171717)',
                    fontFamily: 'Montserrat',
                    fontSize: '20px',
                    fontStyle: 'normal',
                    fontWeight: 700,
                    lineHeight: '125%', // 25px
                  }}
                >
                  Health Info
                </Typography>
                <Box
                  id="info-boxes-container"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: '16px',
                    alignSelf: 'stretch',
                  }}
                >
                  <MiniInfoBlock
                    title="Health Plan"
                    value={defaultHealthInfo && !healthInfo.healthPlan ? 'No Health Plan found' : healthInfo.healthPlan}
                  />
                  <MiniInfoBlock
                    title="Chronic Conditions"
                    value={
                      defaultHealthInfo && !healthInfo.chronicConditions
                        ? 'No Chronic Conditions found'
                        : healthInfo.chronicConditions
                    }
                  />
                  <MiniInfoBlock
                    title="Date of Birth"
                    value={defaultHealthInfo && !healthInfo.DOB ? 'No Date of Birth found' : healthInfo.DOB}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        )}
        {selectedMenuOption === 'Preferences' && (
          <Box
            id="main-personal-info-content-container"
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              alignContent: 'flex-start',
              gap: '40px',
              alignSelf: 'stretch',
              flexWrap: 'wrap',
            }}
          >
            <Box
              id="contact-info-box-container"
              sx={{
                display: 'flex',
                width: '324px',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '40px',
              }}
            >
              <Box
                id="contact-info-container"
                sx={{
                  display: 'flex',
                  width: '327px',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '16px',
                  borderRadius: '8px',
                }}
              >
                <Typography
                  sx={{
                    alignSelf: 'stretch',
                    color: 'var(--color12, #171717)',
                    fontFamily: 'Montserrat',
                    fontSize: '20px',
                    fontStyle: 'normal',
                    fontWeight: 700,
                    lineHeight: '125%', // 25px
                  }}
                >
                  Notification Preferences
                </Typography>
                <Box
                  id="info-boxes-container"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: '16px',
                    alignSelf: 'stretch',
                  }}
                >
                  <PreferenceSwitchBox
                    checked={false}
                    onChange={() => {}}
                    switchItems={['SMS Notifications', 'Push Notifications', 'Email Notifications']}
                  />
                </Box>
              </Box>
            </Box>
            <Box
              id="health-info-box-container"
              sx={{
                display: 'flex',
                width: '327px',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '16px',
                borderRadius: '8px',
              }}
            >
              <Box
                id="health-info-container"
                sx={{
                  display: 'flex',
                  width: '327px',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '16px',
                  borderRadius: '8px',
                }}
              >
                <Typography
                  sx={{
                    alignSelf: 'stretch',
                    color: 'var(--color12, #171717)',
                    fontFamily: 'Montserrat',
                    fontSize: '20px',
                    fontStyle: 'normal',
                    fontWeight: 700,
                    lineHeight: '125%', // 25px
                  }}
                >
                  Reminder Preferences
                </Typography>
                <Box
                  id="info-boxes-container"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: '16px',
                    alignSelf: 'stretch',
                  }}
                >
                  <PreferenceSwitchBox
                    checked={false}
                    onChange={() => {}}
                    switchItems={['Program Messages', 'Marketing Messages', 'Challenge Reminders']}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        )}
        {selectedMenuOption === 'Program History' && (
          <Box
            id="program-history-container"
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              alignContent: 'flex-start',
              gap: '24px',
              alignSelf: 'stretch',
              flexWrap: 'wrap',
            }}
          >
            {programBenefits.map((benefit, index) => (
              <NewProgramInfoCard
                key={index}
                programInfo={benefit}
              />
            ))}
          </Box>
        )}
        {selectedMenuOption === 'Application History' && (
          <Box
            id="application-history-container"
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              alignContent: 'flex-start',
              gap: '24px',
              alignSelf: 'stretch',
              flexWrap: 'wrap',
            }}
          >
            {applicationBenefits.map((benefit, index) => (
              <NewProgramInfoCard
                key={index}
                programInfo={benefit}
              />
            ))}
          </Box>
        )}
        <Drawer
          anchor="right"
          open={isDrawerOpen}
          onClose={handleDrawerClose}
        >
          <EditAddressPopUp
            onClose={handleDrawerClose}
            addAddress={isAddAddress}
          />
        </Drawer>
      </Box>
    </Screen>
  );
};

export default AccountManagementScreen;
